import IconClose from '@assets/icons/close.svg?react';
import IconSearch from '@assets/icons/search.svg?react';
import IconSettings from '@assets/icons/settings.svg?react';
import {
	Autocomplete,
	Box,
	Button,
	Divider,
	IconButton,
	MenuItem,
	Paper,
	type PaperProps,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getClientConfigurationsStart } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPendingSmartSearch } from '../../../redux/smartSearch/smartSearchSlice';
import layoutSizes from '../../../utils/layoutSizes';
import {
	SMART_SEARCH_TARGET_PAGES,
	type SmartSearchHistoryEntry,
	type SmartSearchRule,
	addRecentSmartSearch,
	clearRecentSmartSearches,
	loadRecentSmartSearches,
	matchSmartSearchRules,
	syncSmartSearchRulesFromConfigurations,
} from '../../../utils/smartSearch';
import SmartSearchRulesModal from './SmartSearchRulesModal';

const Search = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const clientConfigurations = useAppSelector(
		(state) => state.clientConfiguration.clientConfigurations,
	);

	const [inputValue, setInputValue] = useState('');
	const [open, setOpen] = useState(false);
	// Mobile: the search is collapsed to an icon and expands to a full-width
	// overlay (the header has no room for an inline input on small screens).
	const [mobileOpen, setMobileOpen] = useState(false);
	const [rulesModalOpen, setRulesModalOpen] = useState(false);
	const [modalInitialDraft, setModalInitialDraft] = useState<
		Partial<SmartSearchRule> | undefined
	>(undefined);
	const [recentSearches, setRecentSearches] = useState<
		SmartSearchHistoryEntry[]
	>(() => loadRecentSmartSearches());
	// Bumped after a backend sync so matched-rule memos recompute.
	const [rulesVersion, setRulesVersion] = useState(0);

	// Load rules from the backend (POST /ClientConfiguration/v1) once, if the
	// shared config list is not already populated by a table page.
	useEffect(() => {
		if (!clientConfigurations) dispatch(getClientConfigurationsStart({}));
	}, [clientConfigurations, dispatch]);

	// Mirror backend rules into the local cache the matcher reads.
	useEffect(() => {
		if (syncSmartSearchRulesFromConfigurations(clientConfigurations)) {
			setRulesVersion((version) => version + 1);
		}
	}, [clientConfigurations]);

	// Live mapping while the user types: all rules this text matches,
	// highest priority first.
	const matchedRules = useMemo(
		() => matchSmartSearchRules(inputValue),
		// rulesVersion bumps when the backend sync updates the local cache.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[inputValue, rulesVersion],
	);

	const pageLabel = useCallback(
		(route: string) => {
			const page = SMART_SEARCH_TARGET_PAGES.find(
				(targetPage) => targetPage.route === route,
			);
			return page ? t(`entities.${page.entityKey}`) : route;
		},
		[t],
	);

	// Built-in default rules resolve their display name through the locale
	// files (keyed by rule id); user rules show the name they were saved with.
	const ruleLabel = useCallback(
		(rule: SmartSearchRule) =>
			rule.isBuiltIn
				? t(`global.defaultRuleNames.${rule.id}`, { defaultValue: rule.name })
				: rule.name,
		[t],
	);

	// No rule maps this text — ask the user to map it to a grid.
	const openCreateRule = useCallback((text: string) => {
		const trimmed = text.trim();
		setModalInitialDraft({
			name: trimmed,
			matchType: 'startsWith',
			matchValue: trimmed,
		});
		setRulesModalOpen(true);
		setOpen(false);
		setMobileOpen(false);
	}, []);

	// Fire a specific rule with the given text.
	const fireRule = useCallback(
		(rule: SmartSearchRule, text: string) => {
			const trimmed = text.trim();
			if (!trimmed) return;
			setRecentSearches(
				addRecentSmartSearch({
					text: trimmed,
					ruleId: rule.id,
					route: rule.route,
				}),
			);
			// Hand the search off to the target grid via redux; the grid runs
			// it through its normal filter pipeline (POST with body).
			dispatch(
				setPendingSmartSearch({
					route: rule.route,
					field: rule.filterField,
					value: trimmed,
					// Empty operator = let the target grid pick one from the field's
					// structure type (string → contains, numeric → equals).
					operator: rule.filterOperator || '',
				}),
			);
			// Keep the typed text in the input after firing.
			setOpen(false);
			setMobileOpen(false);
			navigate(rule.route);
		},
		[dispatch, navigate],
	);

	const runSearch = useCallback(
		(text: string) => {
			const trimmed = text.trim();
			if (!trimmed) return;

			const matches = matchSmartSearchRules(trimmed);
			if (matches.length > 0) {
				// Enter fires the highest-priority match; other matches stay
				// selectable in the dropdown.
				fireRule(matches[0], trimmed);
			} else {
				setRecentSearches(
					addRecentSmartSearch({ text: trimmed, ruleId: null, route: null }),
				);
				openCreateRule(trimmed);
			}
		},
		[fireRule, openCreateRule],
	);

	const handleClearRecent = useCallback(() => {
		setRecentSearches(clearRecentSmartSearches());
	}, []);

	const handleModalClose = useCallback(() => {
		setRulesModalOpen(false);
		setModalInitialDraft(undefined);
	}, []);

	const handleRuleSaved = useCallback(() => {
		setRulesModalOpen(false);
		setModalInitialDraft(undefined);
		// The text is now mapped — run the search immediately.
		if (inputValue.trim()) runSearch(inputValue);
	}, [inputValue, runSearch]);

	const SearchPaper = useCallback(
		(paperProps: PaperProps) => (
			<Paper {...paperProps}>
				{inputValue.trim() && (
					<>
						{matchedRules.length > 0 && (
							// Every matching rule is selectable; Enter fires the first
							// (highest priority) one. User rules sort above the built-in
							// per-page defaults, which match any text.
							<Box sx={{ py: 0.5 }}>
								{matchedRules.map((rule, index) => (
									<MenuItem
										key={rule.id}
										dense
										// preventDefault keeps the input from blurring (and the
										// popup from closing) before the click lands
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => fireRule(rule, inputValue)}
									>
										<Typography
											variant="caption"
											color={index === 0 ? 'text.primary' : 'text.secondary'}
											noWrap
										>
											{`${inputValue.trim()} → ${pageLabel(rule.route)} · ${ruleLabel(rule)}`}
										</Typography>
									</MenuItem>
								))}
							</Box>
						)}
						<Box sx={{ p: '8px 12px' }}>
							<Button
								size="small"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => openCreateRule(inputValue)}
							>
								{t('global.createRuleFor', { text: inputValue.trim() })}
							</Button>
						</Box>
						<Divider />
					</>
				)}
				{paperProps.children}
				{recentSearches.length > 0 && (
					<>
						<Divider />
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
							<Button
								size="small"
								onMouseDown={(event) => event.preventDefault()}
								onClick={handleClearRecent}
							>
								{t('global.clearRecentSearches')}
							</Button>
						</Box>
					</>
				)}
			</Paper>
		),
		[
			inputValue,
			matchedRules,
			fireRule,
			pageLabel,
			ruleLabel,
			openCreateRule,
			recentSearches.length,
			handleClearRecent,
			t,
		],
	);

	return (
		<>
			{/* Mobile: collapsed trigger that opens the full-width overlay. */}
			<IconButton
				aria-label={t('global.search')}
				onClick={() => {
					setMobileOpen(true);
					setOpen(true);
				}}
				sx={{
					display: { xs: mobileOpen ? 'none' : 'inline-flex', md: 'none' },
					marginLeft: 'auto',
					flexShrink: 0,
				}}
			>
				<IconSearch
					style={{
						color: theme.palette.kemek.blue,
						display: 'block',
						width: '18px',
						height: '18px',
					}}
				/>
			</IconButton>

			<Box
				sx={{
					alignItems: 'center',
					gap: '4px',
					// Inline on desktop; full-width fixed overlay across the header
					// on mobile when expanded.
					display: { xs: mobileOpen ? 'flex' : 'none', md: 'flex' },
					position: { xs: 'fixed', md: 'relative' },
					top: { xs: 0, md: 'auto' },
					left: { xs: 0, md: 'auto' },
					right: { xs: 0, md: 'auto' },
					height: { xs: `${layoutSizes.headerHeight}px`, md: 'auto' },
					px: { xs: '12px', md: 0 },
					background: { xs: theme.palette.kemek.bg2, md: 'transparent' },
					zIndex: { xs: theme.zIndex.appBar + 2, md: 'auto' },
					flex: { md: '0 0 auto' },
					minWidth: 0,
					marginLeft: { md: 'auto' },
					mr: { md: '24px' },
				}}
			>
				<Autocomplete
					freeSolo
					disableClearable
					open={
						open && (inputValue.trim().length > 0 || recentSearches.length > 0)
					}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					options={recentSearches.map((entry) => entry.text)}
					inputValue={inputValue}
					onInputChange={(_, value) => setInputValue(value)}
					onChange={(_, value) => {
						if (typeof value === 'string') runSearch(value);
					}}
					PaperComponent={SearchPaper}
					sx={{
						flex: { xs: 1, md: 'none' },
						width: { md: 320, lg: 420 },
						minWidth: 0,
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							size="small"
							placeholder={t('global.search')}
							slotProps={{
								input: {
									...params.InputProps,
									startAdornment: (
										<>
											<IconSearch
												style={{
													color: theme.palette.kemek.blue,
													display: 'block',
													width: '15px',
													height: '15px',
													marginRight: '8px',
													flexShrink: 0,
												}}
											/>
											{params.InputProps.startAdornment}
										</>
									),
									sx: {
										borderRadius: '20px',
										background: theme.palette.background.paper,
										pl: '14px !important',
										boxShadow: '0 1px 4px rgba(43, 43, 43, 0.08)',
										transition: 'box-shadow 0.2s ease-in-out',
										'&:hover': {
											boxShadow: '0 2px 8px rgba(96, 128, 255, 0.25)',
										},
										'&.Mui-focused': {
											boxShadow: '0 2px 10px rgba(96, 128, 255, 0.35)',
										},
										'& .MuiOutlinedInput-notchedOutline': {
											borderColor: theme.palette.kemek.lightestGray,
										},
										'&:hover .MuiOutlinedInput-notchedOutline': {
											borderColor: theme.palette.kemek.blue,
										},
										'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
											borderColor: theme.palette.kemek.blue,
											borderWidth: '1px',
										},
									},
								},
							}}
						/>
					)}
				/>
				<IconButton
					size="small"
					aria-label={t('global.smartSearchRules')}
					onClick={() => setRulesModalOpen(true)}
					sx={{ flexShrink: 0 }}
				>
					<IconSettings
						style={{
							color: theme.palette.kemek.lightGray,
							display: 'block',
							width: '16px',
							height: '16px',
						}}
					/>
				</IconButton>
				{/* Mobile: close the overlay. */}
				<IconButton
					size="small"
					aria-label={t('global.close')}
					onClick={() => {
						setMobileOpen(false);
						setOpen(false);
					}}
					sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
				>
					<IconClose
						style={{
							color: theme.palette.kemek.darkestGray,
							display: 'block',
							width: '18px',
							height: '18px',
						}}
					/>
				</IconButton>
			</Box>
			{rulesModalOpen && (
				<SmartSearchRulesModal
					isOpen={rulesModalOpen}
					handleClose={handleModalClose}
					initialDraft={modalInitialDraft}
					onSaved={handleRuleSaved}
				/>
			)}
		</>
	);
};

export default Search;
